import React, { useEffect, useContext, useState, useRef } from 'react';
import FadeIn from 'react-fade-in';
import { Observer } from 'mobx-react-lite';
import MaskedInput from 'react-text-mask'
import { GlobalHotKeys } from 'react-hotkeys';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModal';
import AddressInput from '../../_shared/AddressInput';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import { quickDrawerFocus } from '../../_shared/QuickDrawer';
import HtmlEditor from '../../_shared/HtmlEditor';

import PrivateInsuranceProviderUpdateStore from '../../../../stores/PrivateInsuranceProviderUpdateStore';
import AuthStore from '../../../../stores/AuthStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as MaskKeys from '../../../../constants/maskKeys';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';

import './UpdatePrivateInsuranceProvider.scss';

function UpdatePrivateInsuranceProvider(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const searchTimer = useRef(null);
    const focusTimer = useRef(null);
    const addressRef = useRef(null);
    const privateInsuranceProvider = useContext(PrivateInsuranceProviderUpdateStore);
    const auth = useContext(AuthStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        focusTimer.current = setTimeout(() => {
            quickDrawerFocus(props.drawer);
        }, 100)

        return () => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
            if (searchTimer.current) { clearTimeout(searchTimer.current); searchTimer.current = null; }
            privateInsuranceProvider.clear();
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (privateInsuranceProvider.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleDelete = (event) => {
        setConfirmDelete(true);
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current) && addressRef.current.validate()) {
            privateInsuranceProvider.save()
                .then((data) => {
                    if (isMounted.current) {
                        if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                            props.onSuccess(event, { updated: true, data: data });
                        }
                    }
                })
        }
    }

    const handleAddressChange = (data) => {
        privateInsuranceProvider.newAddress = data;
        privateInsuranceProvider.hasUnsavedChanges = true;
    }

    const handleNameChange = (event) => {
        const value = event.target.value;

        privateInsuranceProvider.data.name = value;
        privateInsuranceProvider.hasUnsavedChanges = true;
    }

    const handlePhoneNumberChange = (event) => {
        const value = event.target.value;

        privateInsuranceProvider.data.phoneNumber = value;
        privateInsuranceProvider.hasUnsavedChanges = true;
    }

    const handleFaxNumberChange = (event) => {
        const value = event.target.value;

        privateInsuranceProvider.data.faxNumber = value;
        privateInsuranceProvider.hasUnsavedChanges = true;
    }

    const handleEmailAddressChange = (event) => {
        const value = event.target.value;

        privateInsuranceProvider.data.emailAddress = value;
        privateInsuranceProvider.hasUnsavedChanges = true;
    }

    const handleWebsiteUrlChange = (event) => {
        const value = event.target.value;

        privateInsuranceProvider.data.websiteUrl = value;
        privateInsuranceProvider.hasUnsavedChanges = true;
    }

    const handleNoteChange = (element) => {
        const newNoteHtml = element.value;
        privateInsuranceProvider.data.noteHtml = newNoteHtml;
    }

    const handleConfirmDelete = (event) => {
        privateInsuranceProvider.delete()
            .then(() => {
                if (isMounted.current) {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, { updated: true });
                    }
                }
            })
        setConfirmDelete(false);
    }

    const handleConfirmCancel = (event) => {
        setConfirmDelete(false);
    }

    return <>
        {
            (props.drawer === quickDrawer.drawerOpened) ?
                <GlobalHotKeys
                    keyMap={{
                        close: ['esc'],
                    }}
                    handlers={{
                        close: event => {
                            handleCancel(event)
                        },
                    }}
                    allowChanges={true}
                /> : null
        }
        <form ref={validateRef} onSubmit={handleSubmit}>
            <fieldset disabled={privateInsuranceProvider.isSaving}>
                <div className='add-private-insurance-provider-container quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('private-insurance-provider', 'default')}
                        action='Update'
                        category='Insurance Provider'
                        className='private-insurance-providers'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <Observer>{() => <>
                            {
                                privateInsuranceProvider.isReady ?
                                    <FadeIn>
                                        <div className='body-content'>
                                            <section>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-0'>
                                                            <label htmlFor='new-private-insurance-provider-name'><small>Name</small></label>
                                                            <input
                                                                id='new-private-insurance-provider-name'
                                                                type='text'
                                                                className='form-control'
                                                                spellCheck={true}
                                                                maxLength='255'
                                                                autoComplete='off'
                                                                value={privateInsuranceProvider.data.name ? privateInsuranceProvider.data.name : ''}
                                                                onChange={handleNameChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                            <section>
                                                <AddressInput
                                                    ref={addressRef}
                                                    address={(privateInsuranceProvider.data && privateInsuranceProvider.data.address ? privateInsuranceProvider.data.address : {})}
                                                    label={'Address'}
                                                    defaultCountryCode={(auth.currentUser && auth.currentTenant && auth.currentTenant.address ? auth.currentTenant.address.countryCode : null)}
                                                    onChange={handleAddressChange}
                                                />
                                            </section>
                                            <section>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-0 validate validate-phone'>
                                                            <label htmlFor='new-private-insurance-provider-phone'><small>Phone</small></label>
                                                            <MaskedInput
                                                                id='new-private-insurance-provider-phone'
                                                                type='text'
                                                                className='form-control'
                                                                spellCheck={false}
                                                                mask={MaskKeys.PHONE_MASK}
                                                                autoComplete='off'
                                                                value={privateInsuranceProvider.data.phoneNumber ? privateInsuranceProvider.data.phoneNumber : ''}
                                                                onChange={handlePhoneNumberChange}
                                                            // onBlur={checkConflicts}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                            <section>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-0 validate validate-phone'>
                                                            <label htmlFor='new-private-insurance-provider-fax'><small>Fax</small></label>
                                                            <MaskedInput
                                                                id='new-private-insurance-provider-fax'
                                                                type='text'
                                                                className='form-control'
                                                                spellCheck={false}
                                                                mask={MaskKeys.PHONE_MASK}
                                                                autoComplete='off'
                                                                value={privateInsuranceProvider.data.faxNumber ? privateInsuranceProvider.data.faxNumber : ''}
                                                                onChange={handleFaxNumberChange}
                                                            // onBlur={checkConflicts}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                            <section>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-0 validate validate-email'>
                                                            <label htmlFor='new-private-insurance-provider-email'><small>Email</small></label>
                                                            <MaskedInput
                                                                id='new-private-insurance-provider-email'
                                                                type='text'
                                                                className='form-control'
                                                                spellCheck={false}
                                                                mask={MaskKeys.EMAIL_MASK}
                                                                maxLength='150'
                                                                autoComplete='off'
                                                                value={privateInsuranceProvider.data.emailAddress ? privateInsuranceProvider.data.emailAddress : ''}
                                                                onChange={handleEmailAddressChange}
                                                            // onBlur={checkConflicts}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                            <section>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-0'>
                                                            <label htmlFor='new-private-insurance-provider-website-url'><small>Website</small></label>
                                                            <input
                                                                id='new-private-insurance-provider-website-url'
                                                                type='text'
                                                                className='form-control'
                                                                spellCheck={true}
                                                                maxLength='255'
                                                                autoComplete='off'
                                                                value={privateInsuranceProvider.data.websiteUrl ? privateInsuranceProvider.data.websiteUrl : ''}
                                                                onChange={handleWebsiteUrlChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                            <section>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-2'>
                                                            <label htmlFor='new-private-insurance-provider-note'><small>Note</small></label>
                                                            <HtmlEditor
                                                                disableTab={true}
                                                                defaultValue={privateInsuranceProvider.data.noteHtml}
                                                                onValueChanged={handleNoteChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                        </div>
                                    </FadeIn> : null
                            }
                        </>
                        }</Observer>
                    </div>
                    <div className='quick-drawer-action pl-3'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-danger mr-2'
                                        onClick={handleDelete}
                                    >Delete</button>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                    >Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
        <BodyEnd>
            <ConfirmModal
                icon={<i className='fal fa-clinic-medical text-danger mr-2'></i>}
                message={<>Continue to delete <span className='fw-500 ml-2'>{privateInsuranceProvider.originalData ? privateInsuranceProvider.originalData.name : ''}</span>? </>}
                show={confirmDelete}
                onOption1Click={handleConfirmDelete}
                onCancel={handleConfirmCancel}
            />
        </BodyEnd>
    </>
}

export default UpdatePrivateInsuranceProvider;